import { AppState, Platform } from 'react-native';
import { QueryClient, focusManager } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';

// On web TanStack Query already listens for `visibilitychange`, so a return to
// the tab counts as focus. React Native has no such event; without this the
// app is "focused" forever — the list screens would never refetch on
// foregrounding, and the background-paused refetch interval would never pause.
if (Platform.OS !== 'web') {
  focusManager.setEventListener((handleFocus) => {
    const subscription = AppState.addEventListener('change', (state) => {
      handleFocus(state === 'active');
    });
    return () => subscription.remove();
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,        // 2 min — show cached, refetch in background
      gcTime: 24 * 60 * 60 * 1000,     // 24 hours in memory
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      networkMode: 'offlineFirst',      // return cache when offline, don't error
    },
  },
});

/**
 * Top-level query keys whose cached payloads must be refreshed after any
 * entity write. The list-screen hooks bundle related entities (e.g.
 * useGoalsList fetches goals + visions + activities), so a single mutation
 * to one entity can invalidate any of these caches.
 */
const ENTITY_LIST_KEYS = new Set(['activities', 'goals', 'visions']);

/**
 * Wrap an entity-mutating API call so that the persisted query cache is
 * invalidated on success. Every create/update/delete on activities, goals,
 * visions, or recurrence schedules should go through this helper — that's
 * the chokepoint that keeps the React Query cache in sync with the server.
 *
 * Usage:
 *   const activity = await entityMutation(() => activitiesApi.create(data));
 *
 * The wrapper preserves the original return value and re-throws errors
 * unchanged, so existing try/catch and isSubmitting patterns keep working.
 */
export async function entityMutation<T>(fn: () => Promise<T>): Promise<T> {
  const result = await fn();
  await queryClient.invalidateQueries({
    predicate: (q) => ENTITY_LIST_KEYS.has(q.queryKey[0] as string),
  });
  return result;
}

/**
 * Persists the query cache to AsyncStorage so it survives app restarts.
 *
 * IMPORTANT: Only encrypted payloads are cached (never decrypted plaintext).
 * Decryption happens on-demand in query hooks after the encryption store
 * is initialized.
 */
export const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
  key: '@cynosure/react-query-cache',
});
